import { useLocation } from "@remix-run/react";

export interface LanguageOption {
  code: string;
  name: string;
  flag: string;
  path: string;
}

export const languages: LanguageOption[] = [
  { code: "en", name: "English", flag: "🇺🇸", path: "/" },
  { code: "ja", name: "日本語", flag: "🇯🇵", path: "/ja" },
  { code: "ko", name: "한국어", flag: "🇰🇷", path: "/ko" },
];

/**
 * 从路径中获取当前语言
 */
export function getCurrentLanguageFromPath(pathname: string | undefined): LanguageOption {
  if (!pathname || typeof pathname !== 'string') {
    return languages[0]; // 默认英语
  }
  if (pathname.startsWith('/ja')) return languages[1];
  if (pathname.startsWith('/ko')) return languages[2];
  return languages[0]; // 默认英语
}

/**
 * 获取当前语言的路径前缀
 */
export function getCurrentLanguagePrefix(pathname: string | undefined): string {
  if (!pathname || typeof pathname !== 'string') {
    return ''; // 默认英语无前缀
  }
  if (pathname.startsWith('/ja')) return '/ja';
  if (pathname.startsWith('/ko')) return '/ko';
  return ''; // 英语默认无前缀
}

/**
 * 获取指定语言的路径
 */
export function getPathForLanguage(currentPath: string | undefined, targetLang: LanguageOption): string {
  if (!currentPath || typeof currentPath !== 'string') {
    // 如果没有当前路径，返回目标语言的根路径
    return targetLang.code === 'en' ? '/' : targetLang.path;
  }
  
  const currentLang = getCurrentLanguageFromPath(currentPath);
  
  // 从当前路径中移除语言前缀
  let basePath = currentPath;
  if (currentLang.code !== 'en') {
    basePath = currentPath.replace(`/${currentLang.code}`, '') || '/';
  }
  
  // 添加目标语言前缀
  if (targetLang.code === 'en') {
    return basePath === '/' ? '/' : basePath;
  } else {
    return targetLang.path + (basePath === '/' ? '' : basePath);
  }
}

/**
 * React Hook: 获取当前语言信息
 */
export function useCurrentLanguage() {
  const location = useLocation();
  
  // 确保 pathname 存在，避免服务端渲染时的错误
  const pathname = location?.pathname || '/';
  
  const currentLanguage = getCurrentLanguageFromPath(pathname);
  const languagePrefix = getCurrentLanguagePrefix(pathname);
  
  return {
    currentLanguage,
    languagePrefix,
    pathname,
  };
}

/**
 * 生成带语言前缀的路径
 */
export function createLocalizedPath(path: string, languagePrefix: string): string {
  // 如果路径已经包含语言前缀，直接返回
  if (path.startsWith('/ja') || path.startsWith('/ko')) {
    return path;
  }
  
  // 如果是英语（无前缀），直接返回原路径
  if (!languagePrefix) {
    return path;
  }
  
  // 添加语言前缀
  return `${languagePrefix}${path}`;
} 